/* eslint-disable no-else-return */
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from 'theme/withStyles';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import PlusMinus from 'common/PlusMinus';
import homeActions from 'core/home/actions';

import buttonLinkStyles from 'styles/buttonLink';
import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    buttonLink: buttonLinkStyles(theme),
    disabledItem: {
      color: theme.palette.grey[500],
      opacity: 0.5,
      pointerEvents: 'none',
    },

    hidden: {
      display: 'none',
    },
  };
};

export const HomeDisclosuresPanelBare = ({ classes, openDialog, request, resources }) => {
  const { data, isOpen } = useSelector(({ home }) => ({
    data:
      (home[request.id] && home[request.id].disclosures && home[request.id].disclosures.data) ||
      null,
    isOpen:
      (home[request.id] && home[request.id].disclosures && home[request.id].disclosures.isOpen) ||
      false,
  }));
  const dispatch = useDispatch();

  const hasPendingDisclosures =
    (data && data.filter((d) => d.status === 'PENDING' && d.mandatory).length > 0) || !data;

  const headerText = useMemo(
    () => (
      <>
        <RawHtml type="span">{resources.disclosuresTitle}</RawHtml>
        {request.pendingDisclosures && hasPendingDisclosures && (
          <RawHtml
            type="span"
            className="pending-label"
          >{` * ${resources.pendingDisclosureLabel}`}</RawHtml>
        )}
      </>
    ),
    [
      hasPendingDisclosures,
      request.pendingDisclosures,
      resources.disclosuresTitle,
      resources.pendingDisclosureLabel,
    ],
  );

  const panelBody = useMemo(() => {
    if (data === null || data === undefined) {
      return <Spinner loading />;
    }
    if (data.length === 0) {
      return <p>This application currently has no disclosures.</p>;
    }

    return (
      <ul>
        {data.map((d) => (
          <li
            key={d.id}
            className={classnames(classes.panelItem, { [classes.disabledItem]: d.disabled })}
            data-name={d.label}
            data-id={d.id}
            data-status={d.status}
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <button
              type="button"
              disabled={!!d.disabled}
              className={classnames('buttonLink name', classes.buttonLink)}
              onClick={(e) => {
                e.preventDefault();
                openDialog('home_disclosures', {
                  disclosure: d,
                  request,
                });
              }}
            >
              {d.label}
            </button>{' '}
            <span
              className={classnames(
                'status',
                classes.status,
                {
                  'accepted-at': d.acceptedAt,
                },
                { notMandatory: true },
              )}
            >
              <span className="status-label">{d.statusDescr}</span>
              {d.acceptedAt && (
                <span className="status-datetime">
                  {': '}
                  {d.acceptedAt}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    );
  }, [classes.buttonLink, classes.panelItem, classes.status, data, openDialog, request]);

  return (
    <div
      className={classnames('panel panel-primary loading panel-disclosures', classes.root)}
      role="tab"
    >
      <div className="panel-heading" id={`collapseHeading-disclosures-${request.id}`}>
        <div
          role="button"
          tabIndex="0"
          aria-expanded={isOpen}
          aria-controls={`collapseAreaID-disclosures-${request.id}`}
          className={classnames('panel-title', classes.panelTitle)}
          onClick={(e) => {
            e.preventDefault();
            dispatch(homeActions.fetch(request.id, 'disclosures'));
          }}
          onKeyUp={(e) =>
            (e.keyCode === 13 || e.keyCode === 32) &&
            dispatch(homeActions.fetch(request.id, 'disclosures'))
          }
        >
          <div
            data-toggle="collapse"
            href={`#collapseHeading-disclosures-${request.id}`}
            className={classnames('buttonLink get-disclosures', classes.buttonLink, {
              collapsed: !isOpen,
              [classes.collapsed]: !isOpen,
            })}
          >
            <PlusMinus collapsed={!isOpen} /> {headerText}
          </div>
        </div>
      </div>
      <Collapse isOpened={isOpen}>
        <div id={`collapseAreaID-disclosures-${request.id}`} className="panel-collapse">
          <div
            className={classnames('panel-body', classes.panelBody, {
              [classes.hidden]: !isOpen,
            })}
          >
            {panelBody}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

HomeDisclosuresPanelBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  openDialog: PropTypes.func.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number,
    pendingDisclosures: PropTypes.bool,
  }).isRequired,
  resources: PropTypes.shape({
    disclosuresTitle: PropTypes.string,
    pendingDisclosureLabel: PropTypes.string,
  }).isRequired,
};

const HomeDisclosuresPanelStyled = withStyles(styles)(HomeDisclosuresPanelBare);

export default HomeDisclosuresPanelStyled;
